import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import EditUserModal from 'flarum/common/components/EditUserModal';
import Stream from 'flarum/common/utils/Stream';

function isAdministrator() {
  return Boolean(app.session.user?.isAdmin());
}

function userAdminTrans(key) {
  return app.translator.trans(`pakt21-beta.admin.user.${key}`);
}

function profileTrans(key) {
  return app.translator.trans(`pakt21-beta.forum.profile_form.${key}`);
}

function idStatusTrans(status) {
  return app.translator.trans(`pakt21-beta.admin.id_management.status_${status}`);
}

function parseSkills(value) {
  const seen = new Set();

  return String(value || '')
    .split(/[,\n]/)
    .map((skill) => skill.trim().replace(/\s+/g, ' '))
    .filter((skill) => {
      const key = skill.toLocaleLowerCase();
      if (!skill || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

app.initializers.add('pakt21-beta-forum-admin-user-attributes', () => {
  extend(EditUserModal.prototype, 'oninit', function () {
    if (!isAdministrator()) return;

    this.pakt21VerificationStatus = Stream(this.attrs.user.attribute('pakt21VerificationStatus') || 'unverified');
    this.pakt21TrustLevel = Stream(Number(this.attrs.user.attribute('pakt21TrustLevel') || 0));
    this.pakt21TrustChangeReason = Stream('');
    this.pakt21MemberNumber = Stream(this.attrs.user.attribute('pakt21AssignedMemberNumber') ?? '');
    this.pakt21MemberNumberStatus = Stream(this.attrs.user.attribute('pakt21MemberNumberStatus') || 'active');
    this.pakt21MemberNumberReason = Stream('');
    this.pakt21LoungeDelaySeconds = Stream(this.attrs.user.attribute('pakt21LoungeDelaySeconds') ?? '');
    this.pakt21LoungeDelayReason = Stream('');
    this.pakt21About = Stream(this.attrs.user.attribute('pakt21About') || '');
    this.pakt21Contribution = Stream(this.attrs.user.attribute('pakt21Contribution') || '');
    this.pakt21Needs = Stream(this.attrs.user.attribute('pakt21Needs') || '');
    this.pakt21Location = Stream(this.attrs.user.attribute('pakt21Location') || '');
    this.pakt21Skills = Stream((this.attrs.user.attribute('pakt21Skills') || []).join(', '));
  });

  extend(EditUserModal.prototype, 'fields', function (items) {
    if (!isAdministrator()) return;

    const textField = (label, stream, options = {}) => (
      <div className="Form-group">
        <label>{label}</label>
        {options.multiline ? (
          <textarea className="FormControl" rows={options.rows || 3} maxlength={options.maxlength} bidi={stream} />
        ) : (
          <input className="FormControl" type="text" maxlength={options.maxlength} bidi={stream} />
        )}
      </div>
    );

    items.add(
      'pakt21VerificationStatus',
      <div className="Form-group">
        <label>{userAdminTrans('status_heading')}</label>
        <select
          className="FormControl"
          value={this.pakt21VerificationStatus()}
          onchange={(event) => this.pakt21VerificationStatus(event.target.value)}
        >
          <option value="unverified">{userAdminTrans('status_unverified')}</option>
          <option value="verified_adult">{userAdminTrans('status_verified_adult')}</option>
        </select>
      </div>,
      15
    );

    items.add(
      'pakt21LoungeDelaySeconds',
      <div className="Form-group Pakt21Admin-loungeDelay">
        <label>{userAdminTrans('lounge_delay_heading')}</label>
        <input
          className="FormControl"
          type="number"
          min="0"
          max="3600"
          step="1"
          placeholder={userAdminTrans('lounge_delay_default_placeholder')}
          value={this.pakt21LoungeDelaySeconds()}
          oninput={(event) => this.pakt21LoungeDelaySeconds(event.target.value)}
        />
        <p className="helpText">{userAdminTrans('lounge_delay_help')}</p>
      </div>,
      11
    );

    items.add(
      'pakt21LoungeDelayReason',
      <div className="Form-group Pakt21Admin-loungeDelayReason">
        <label>{userAdminTrans('lounge_delay_reason_heading')}</label>
        <textarea className="FormControl" rows="3" maxlength="500" bidi={this.pakt21LoungeDelayReason} />
        <p className="helpText">{userAdminTrans('lounge_delay_reason_help')}</p>
      </div>,
      10
    );

    items.add(
      'pakt21MemberNumber',
      <div className="Form-group Pakt21Admin-memberNumber">
        <label>{userAdminTrans('member_number_heading')}</label>
        <input
          className="FormControl"
          type="number"
          min="1"
          max="999999999"
          step="1"
          value={this.pakt21MemberNumber()}
          oninput={(event) => this.pakt21MemberNumber(event.target.value)}
        />
        <p className="helpText">{userAdminTrans('member_number_help')}</p>
      </div>,
      14
    );

    if (this.pakt21MemberNumber() !== '') {
      items.add(
        'pakt21MemberNumberStatus',
        <div className="Form-group Pakt21Admin-memberNumberStatus">
          <label>{userAdminTrans('member_number_status_heading')}</label>
          <select
            className="FormControl"
            value={this.pakt21MemberNumberStatus()}
            onchange={(event) => this.pakt21MemberNumberStatus(event.target.value)}
          >
            <option value="active">{idStatusTrans('active')}</option>
            <option value="suspended">{idStatusTrans('suspended')}</option>
            <option value="retired">{idStatusTrans('retired')}</option>
          </select>
        </div>,
        13.8
      );
    }

    items.add(
      'pakt21MemberNumberReason',
      <div className="Form-group Pakt21Admin-memberNumberReason">
        <label>{userAdminTrans('member_number_reason_heading')}</label>
        <textarea className="FormControl" rows="3" maxlength="500" bidi={this.pakt21MemberNumberReason} />
        <p className="helpText">{userAdminTrans('member_number_reason_help')}</p>
      </div>,
      13.7
    );

    items.add(
      'pakt21TrustLevel',
      <div className="Form-group Pakt21Admin-trustLevel">
        <label>{userAdminTrans('trust_level_heading')}</label>
        <input
          className="FormControl"
          type="number"
          min="0"
          max="21"
          step="1"
          value={this.pakt21TrustLevel()}
          oninput={(event) => this.pakt21TrustLevel(Number(event.target.value))}
        />
        <p className="helpText">
          {userAdminTrans('trust_level_help')}
        </p>
      </div>,
      13
    );

    items.add(
      'pakt21TrustChangeReason',
      <div className="Form-group Pakt21Admin-trustChangeReason">
        <label>{userAdminTrans('trust_change_reason_heading')}</label>
        <textarea
          className="FormControl"
          rows="3"
          maxlength="500"
          bidi={this.pakt21TrustChangeReason}
        />
        <p className="helpText">
          {userAdminTrans('trust_change_reason_help')}
        </p>
      </div>,
      12
    );

    items.add('pakt21About', textField(profileTrans('about_label'), this.pakt21About, { multiline: true, rows: 4, maxlength: 500 }), 9);
    items.add('pakt21Contribution', textField(profileTrans('contribution_label'), this.pakt21Contribution, { multiline: true, rows: 4, maxlength: 1000 }), 8);
    items.add('pakt21Needs', textField(profileTrans('needs_label'), this.pakt21Needs, { multiline: true, rows: 4, maxlength: 1000 }), 7);
    items.add('pakt21Location', textField(profileTrans('location_label'), this.pakt21Location, { maxlength: 100 }), 6);
    items.add('pakt21Skills', textField(profileTrans('skills_label'), this.pakt21Skills, { multiline: true, rows: 3 }), 5);
  });

  extend(EditUserModal.prototype, 'data', function (data) {
    if (!isAdministrator()) return;

    data.pakt21VerificationStatus = this.pakt21VerificationStatus();
    data.pakt21TrustLevel = this.pakt21TrustLevel();
    data.pakt21TrustChangeReason = this.pakt21TrustChangeReason();
    data.pakt21MemberNumber = this.pakt21MemberNumber() === '' ? null : Number(this.pakt21MemberNumber());
    data.pakt21MemberNumberStatus = this.pakt21MemberNumberStatus();
    data.pakt21MemberNumberReason = this.pakt21MemberNumberReason();
    data.pakt21LoungeDelaySeconds = this.pakt21LoungeDelaySeconds() === '' ? null : Number(this.pakt21LoungeDelaySeconds());
    data.pakt21LoungeDelayReason = this.pakt21LoungeDelayReason();
    data.pakt21About = this.pakt21About().trim() || null;
    data.pakt21Contribution = this.pakt21Contribution().trim() || null;
    data.pakt21Needs = this.pakt21Needs().trim() || null;
    data.pakt21Location = this.pakt21Location().trim() || null;
    data.pakt21Skills = parseSkills(this.pakt21Skills());
  });
});
